import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Users, MapPin, CurrencyDollar, CalendarCheck } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { TopHeader } from "@/components/TopHeader";

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, h:mm a") + " IST";
    } catch {
      return dateStr;
    }
  };

  const formatScheduleDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="dashboard-page">
      {/* Header */}
      <TopHeader 
        title={`Hi, ${user?.name || "Salesman"}`} 
        subtitle="DASHBOARD" 
        showLogout={true}
      />

      <div className="p-4 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div 
            className="stat-card animate-fade-in-up stagger-1 cursor-pointer hover:border-[#0055FF]"
            onClick={() => navigate("/customers")}
            data-testid="stat-customers"
          >
            <div className="flex items-center justify-between mb-2">
              <Users size={24} className="text-[#0055FF]" weight="fill" />
            </div>
            <p className="stat-value">{stats?.customer_count || 0}</p>
            <p className="stat-label">Customers</p>
          </div>

          <div 
            className="stat-card animate-fade-in-up stagger-2"
            data-testid="stat-visits"
          >
            <div className="flex items-center justify-between mb-2">
              <MapPin size={24} className="text-[#10B981]" weight="fill" />
            </div>
            <p className="stat-value">{stats?.visit_count || 0}</p>
            <p className="stat-label">Total Visits</p>
          </div>

          <div 
            className="stat-card animate-fade-in-up stagger-3 cursor-pointer hover:border-[#0055FF]"
            onClick={() => navigate("/sales")}
            data-testid="stat-sales"
          >
            <div className="flex items-center justify-between mb-2">
              <CurrencyDollar size={24} className="text-[#F59E0B]" weight="fill" />
            </div>
            <p className="stat-value">${stats?.total_sales?.toLocaleString() || 0}</p>
            <p className="stat-label">Total Sales</p>
          </div>

          <div 
            className="stat-card animate-fade-in-up stagger-4 cursor-pointer hover:border-[#0055FF]"
            onClick={() => navigate("/schedule")}
            data-testid="stat-schedules"
          >
            <div className="flex items-center justify-between mb-2">
              <CalendarCheck size={24} className="text-[#8B5CF6]" weight="fill" />
            </div>
            <p className="stat-value">{stats?.pending_schedules || 0}</p>
            <p className="stat-label">Upcoming Visits</p>
          </div>
        </div>

        {/* Upcoming Schedules */}
        <div className="animate-fade-in-up stagger-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Upcoming Visits
            </h2>
            <button
              onClick={() => navigate("/schedule")}
              className="text-sm text-[#0055FF] font-medium"
              data-testid="view-all-schedules"
            >
              View All
            </button>
          </div>

          <div className="card-flat overflow-hidden">
            {stats?.upcoming_schedules?.length > 0 ? (
              stats.upcoming_schedules.map((schedule, index) => (
                <div
                  key={schedule.id}
                  className="list-item"
                  data-testid={`upcoming-schedule-${index}`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-[#0A0A0A]">{schedule.customer_name}</p>
                    <p className="text-sm text-[#4B5563]">
                      {formatScheduleDate(schedule.scheduled_date)}
                    </p>
                  </div>
                  <span className="badge badge-info">Pending</span>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-[#9CA3AF]">
                No upcoming visits scheduled
              </div>
            )}
          </div>
        </div>

        {/* Recent Visits */}
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Chivo', sans-serif" }}>
              Recent Activity
            </h2>
          </div>

          <div className="card-flat overflow-hidden">
            {stats?.recent_visits?.length > 0 ? (
              stats.recent_visits.map((visit, index) => (
                <div
                  key={visit.id}
                  className="list-item cursor-pointer"
                  onClick={() => navigate(`/customers/${visit.customer_id}`)}
                  data-testid={`recent-visit-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center mr-3">
                    <MapPin size={20} className="text-[#0055FF]" weight="fill" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-[#0A0A0A]">Visited {visit.customer_name}</p>
                    <p className="text-sm text-[#4B5563]">
                      {formatDate(visit.visit_date)}
                    </p>
                  </div>
                  {visit.photo_path && (
                    <span className="badge badge-success">Photo</span>
                  )}
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-[#9CA3AF]">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
